import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Container, Divider,
    FormControlLabel, Switch, IconButton,
    InputAdornment, Button, TextField, Avatar,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    PersonOutlined, AccountBalanceOutlined,
    NotificationsOutlined, SecurityOutlined,
    Visibility, VisibilityOff,
    ContentCopyOutlined, CheckCircleOutline,
    ChevronRightOutlined, LogoutOutlined,
    VerifiedOutlined, EditOutlined,
    LockOutlined, ArrowBackOutlined,
} from '@mui/icons-material';

/* ─── Brand ──────────────────────────────────────────────────────── */
const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    gold:    '#F5C842',
};

/* ─── Data ───────────────────────────────────────────────────────── */
const user = {
    name:          'Ahmed Bello',
    email:         'abello92@example.com',
    phone:         '080-1234-5678',
    bvn:           '221 *** *** 11',
    accountNumber: '0012 3456 78',
    bank:          'First Bank of Nigeria',
    verified:      true,
    memberSince:   'March 2023',
    creditScore:   653,
};

/* ─── Reusable section wrapper ───────────────────────────────────── */
const Section: React.FC<{
    children: React.ReactNode;
    surfaceBg: string;
    surfaceBdr: string;
    surfaceShd: string;
    sx?: object;
}> = ({ children, surfaceBg, surfaceBdr, surfaceShd, sx = {} }) => (
    <Box sx={{
        borderRadius: 3,
        background: surfaceBg,
        border: surfaceBdr,
        boxShadow: surfaceShd,
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        mb: 2,
        ...sx,
    }}>
        {children}
    </Box>
);

/* ─── Section header row ─────────────────────────────────────────── */
const SectionHeader: React.FC<{
    icon: React.ReactNode;
    label: string;
    color?: string;
    isDark: boolean;
}> = ({ icon, label, color = B.purple, isDark }) => (
    <Box sx={{ px: 2.5, pt: 2.25, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}` }}>
        <Box sx={{ width: 32, height: 32, borderRadius: 2, background: alpha(color, .1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {React.cloneElement(icon as React.ReactElement, { sx: { color, fontSize: 16 } })}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '.85rem', letterSpacing: '-.02em', color: isDark ? '#fff' : B.ink }}>
            {label}
        </Typography>
    </Box>
);

/* ─── Info row ───────────────────────────────────────────────────── */
const InfoRow: React.FC<{
    label: string;
    value: string;
    copyable?: boolean;
    isDark: boolean;
}> = ({ label, value, copyable = false, isDark }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(value.replace(/\s|\*/g, ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontSize: '.72rem', color: alpha(isDark ? '#fff' : B.ink, .35), letterSpacing: '.06em', textTransform: 'uppercase', flexShrink: 0, mr: 2 }}>
                {label}
            </Typography>
            <Box display="flex" alignItems="center" gap={.75}>
                <Typography sx={{ fontSize: '.84rem', fontWeight: 600, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {value}
                </Typography>
                {copyable && (
                    <IconButton onClick={handleCopy} sx={{ p: .25, color: alpha(isDark ? '#fff' : B.ink, .3), '&:hover': { color: isDark ? '#fff' : B.ink } }}>
                        {copied
                            ? <CheckCircleOutline sx={{ fontSize: 13, color: B.green }} />
                            : <ContentCopyOutlined sx={{ fontSize: 13 }} />
                        }
                    </IconButton>
                )}
            </Box>
        </Box>
    );
};

/* ─── Toggle row ─────────────────────────────────────────────────── */
const ToggleRow: React.FC<{
    label: string;
    sub?: string;
    checked: boolean;
    onChange: () => void;
    isDark: boolean;
}> = ({ label, sub, checked, onChange, isDark }) => (
    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
            <Typography sx={{ fontSize: '.84rem', fontWeight: 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>{label}</Typography>
            {sub && <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .15 }}>{sub}</Typography>}
        </Box>
        <Switch checked={checked} onChange={onChange} size="small"
                sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: B.purple },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: B.purple },
                }} />
    </Box>
);

/* ─── Action row ─────────────────────────────────────────────────── */
const ActionRow: React.FC<{
    label: string;
    sub?: string;
    color?: string;
    onClick: () => void;
    isDark: boolean;
}> = ({ label, sub, color, onClick, isDark }) => (
    <Box onClick={onClick} sx={{
        px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        cursor: 'pointer', transition: 'background .15s',
        '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) },
    }}>
        <Box>
            <Typography sx={{ fontSize: '.84rem', fontWeight: 500, color: color || (isDark ? '#fff' : B.ink), letterSpacing: '-.01em' }}>{label}</Typography>
            {sub && <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .15 }}>{sub}</Typography>}
        </Box>
        <ChevronRightOutlined sx={{ fontSize: 16, color: alpha(isDark ? '#fff' : B.ink, .22) }} />
    </Box>
);

/* ─── Main ───────────────────────────────────────────────────────── */
const UserProfilePage: React.FC = () => {
    const navigate    = useNavigate();
    const theme       = useTheme();
    const isDark      = theme.palette.mode === 'dark';

    const [showPw,         setShowPw]         = useState(false);
    const [editingPw,      setEditingPw]       = useState(false);
    const [notifications,  setNotifications]  = useState({ email: true, sms: false, push: true });
    const [biometric,      setBiometric]       = useState(false);
    const [twoFactor,      setTwoFactor]       = useState(false);

    const toggle = (key: keyof typeof notifications) =>
        setNotifications(prev => ({ ...prev, [key]: !prev[key] }));

    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;

    return (
        <Box sx={{ minHeight: '100vh', background: isDark ? B.deep : B.lavender, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-56px', sm: 0 },
                pt: { xs: 'calc(56px + 44px)', sm: '44px' },
                pb: 14, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 160, height: 160, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.accent, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                {/* Avatar + name */}
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 2 }}>
                        <Avatar sx={{
                            width: 80, height: 80, mx: 'auto',
                            background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                            fontFamily: "'Instrument Serif', serif",
                            fontSize: '2rem', fontWeight: 400,
                            boxShadow: `0 8px 32px ${alpha(B.dark, .45)}`,
                        }}>
                            {user.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box sx={{
                            position: 'absolute', bottom: 2, right: 2,
                            width: 20, height: 20, borderRadius: '50%',
                            background: B.green, border: `2.5px solid ${B.dark}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <VerifiedOutlined sx={{ fontSize: 10, color: '#fff' }} />
                        </Box>
                    </Box>
                    <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', letterSpacing: '-.03em', fontFamily: "'Instrument Serif', serif", mb: .4 }}>
                        {user.name}
                    </Typography>
                    <Typography sx={{ color: alpha('#fff', .4), fontSize: '.72rem', letterSpacing: '.04em', mb: 1.5 }}>
                        {user.email}
                    </Typography>
                    <Box display="flex" justifyContent="center" gap={1.5}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 8, px: 1.25, py: .45 }}>
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 5px ${B.green}` }} />
                            <Typography sx={{ color: B.green, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.06em' }}>VERIFIED</Typography>
                        </Box>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .6, background: alpha('#fff', .08), border: `1px solid ${alpha('#fff', .12)}`, borderRadius: 8, px: 1.25, py: .45 }}>
                            <Typography sx={{ color: alpha('#fff', .5), fontSize: '.62rem', fontWeight: 600, letterSpacing: '.04em' }}>Member since {user.memberSince}</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ══ CONTENT ════════════════════════════════════════════ */}
            <Container maxWidth="sm" sx={{ mt: -8, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Credit score mini-card ── */}
                <Box onClick={() => navigate('/dashboard')} sx={{
                    mb: 2, borderRadius: 3, cursor: 'pointer', overflow: 'hidden',
                    background: `linear-gradient(145deg, ${B.dark} 0%, ${B.mid} 100%)`,
                    boxShadow: `0 12px 40px ${alpha(B.deep, .45)}`,
                    transition: 'transform .25s',
                    '&:hover': { transform: 'translateY(-2px)' },
                }}>
                    <Box sx={{ px: 2.5, py: 2.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ color: alpha('#fff', .38), fontSize: '.62rem', letterSpacing: '.14em', textTransform: 'uppercase', mb: .4 }}>NG Credit Score</Typography>
                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography sx={{ color: '#fff', fontFamily: "'Instrument Serif', serif", fontSize: '2.4rem', fontWeight: 400, lineHeight: 1, letterSpacing: '-.02em' }}>
                                    {user.creditScore}
                                </Typography>
                                <Typography sx={{ color: B.green, fontSize: '.78rem', fontWeight: 700, alignSelf: 'flex-end', pb: .4 }}>▲ Good</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <ChevronRightOutlined sx={{ color: alpha('#fff', .25), fontSize: 20 }} />
                            <Typography sx={{ color: alpha('#fff', .28), fontSize: '.65rem', mt: 3 }}>View full report →</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ height: 3, background: `linear-gradient(90deg, ${B.purple}, ${B.green})` }} />
                </Box>

                {/* ── Personal information ── */}
                <Section surfaceBg={surfaceBg} surfaceBdr={surfaceBdr} surfaceShd={surfaceShd}>
                    <SectionHeader icon={<PersonOutlined />} label="Personal Information" isDark={isDark} />
                    <InfoRow label="Full name"  value={user.name}  isDark={isDark} />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <InfoRow label="Email"      value={user.email} isDark={isDark} />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <InfoRow label="Phone"      value={user.phone} isDark={isDark} />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <InfoRow label="BVN"        value={user.bvn}   isDark={isDark} copyable />
                    <Box sx={{ px: 2.5, pb: 2.25, pt: 1 }}>
                        <Button size="small" startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
                                sx={{ color: B.purple, textTransform: 'none', fontSize: '.75rem', fontWeight: 600, px: 1.25, py: .6, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            Edit profile
                        </Button>
                    </Box>
                </Section>

                {/* ── Linked accounts ── */}
                <Section surfaceBg={surfaceBg} surfaceBdr={surfaceBdr} surfaceShd={surfaceShd}>
                    <SectionHeader icon={<AccountBalanceOutlined />} label="Linked Accounts" color="#34C759" isDark={isDark} />
                    <InfoRow label="Bank"       value={user.bank}          isDark={isDark} />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <InfoRow label="Account no" value={user.accountNumber} isDark={isDark} copyable />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <ActionRow label="Manage linked accounts" sub="Add or remove bank accounts" onClick={() => navigate('/link-bank')} isDark={isDark} />
                </Section>

                {/* ── Notifications ── */}
                <Section surfaceBg={surfaceBg} surfaceBdr={surfaceBdr} surfaceShd={surfaceShd}>
                    <SectionHeader icon={<NotificationsOutlined />} label="Notifications" color="#FF9500" isDark={isDark} />
                    <ToggleRow
                        label="Email notifications"
                        sub="Statements and security alerts"
                        checked={notifications.email}
                        onChange={() => toggle('email')}
                        isDark={isDark}
                    />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <ToggleRow
                        label="SMS notifications"
                        sub="Transaction confirmations"
                        checked={notifications.sms}
                        onChange={() => toggle('sms')}
                        isDark={isDark}
                    />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <ToggleRow
                        label="Push notifications"
                        sub="Real-time app alerts"
                        checked={notifications.push}
                        onChange={() => toggle('push')}
                        isDark={isDark}
                    />
                </Section>

                {/* ── Security ── */}
                <Section surfaceBg={surfaceBg} surfaceBdr={surfaceBdr} surfaceShd={surfaceShd}>
                    <SectionHeader icon={<SecurityOutlined />} label="Security" color={B.red} isDark={isDark} />
                    <ToggleRow
                        label="Biometric login"
                        sub="Use fingerprint or Face ID"
                        checked={biometric}
                        onChange={() => setBiometric(v => !v)}
                        isDark={isDark}
                    />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />
                    <ToggleRow
                        label="Two-factor authentication"
                        sub="Extra security on sign-in"
                        checked={twoFactor}
                        onChange={() => setTwoFactor(v => !v)}
                        isDark={isDark}
                    />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />

                    {/* Change password inline */}
                    <Box sx={{ px: 2.5, py: 1.75 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center"
                             onClick={() => setEditingPw(v => !v)} sx={{ cursor: 'pointer' }}>
                            <Box>
                                <Typography sx={{ fontSize: '.84rem', fontWeight: 500, color: isDark ? '#fff' : B.ink, letterSpacing: '-.01em' }}>Change password</Typography>
                                <Typography sx={{ fontSize: '.68rem', color: alpha(isDark ? '#fff' : B.ink, .35), mt: .15 }}>Last changed 3 months ago</Typography>
                            </Box>
                            <Box sx={{ transition: 'transform .2s', transform: editingPw ? 'rotate(90deg)' : 'none' }}>
                                <ChevronRightOutlined sx={{ fontSize: 16, color: alpha(isDark ? '#fff' : B.ink, .22) }} />
                            </Box>
                        </Box>

                        {editingPw && (
                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <TextField fullWidth size="small" label="Current password" type={showPw ? 'text' : 'password'}
                                           InputProps={{ endAdornment: (
                                                   <InputAdornment position="end">
                                                       <IconButton size="small" onClick={() => setShowPw(v => !v)} edge="end" sx={{ color: alpha(isDark ? '#fff' : B.ink, .3) }}>
                                                           {showPw ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                                                       </IconButton>
                                                   </InputAdornment>
                                               )}}
                                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '.84rem' }, '& label': { fontSize: '.82rem' } }}
                                />
                                <TextField fullWidth size="small" label="New password" type="password"
                                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '.84rem' }, '& label': { fontSize: '.82rem' } }} />
                                <TextField fullWidth size="small" label="Confirm new password" type="password"
                                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, fontSize: '.84rem' }, '& label': { fontSize: '.82rem' } }} />
                                <Button variant="contained" fullWidth size="small"
                                        sx={{ background: B.purple, borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontSize: '.78rem', py: 1, boxShadow: 'none', '&:hover': { background: B.dark, boxShadow: `0 6px 20px ${alpha(B.purple, .4)}` } }}>
                                    Update password
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Section>

                {/* ── Danger zone ── */}
                <Section surfaceBg={surfaceBg} surfaceBdr={`1px solid ${alpha(B.red, .15)}`} surfaceShd={surfaceShd}>
                    <ActionRow label="Log out" color={B.red} onClick={() => navigate('/')} isDark={isDark} />
                    <Divider sx={{ mx: 2.5, borderColor: alpha(B.red, .1) }} />
                    <ActionRow label="Delete account" sub="Permanently close your NG Credit account" color={alpha(B.red, .6)} onClick={() => {}} isDark={isDark} />
                </Section>

                {/* footer */}
                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default UserProfilePage;